'use client';

import React, { memo, useMemo } from 'react';

interface AppLogoProps {
  size?: number;
  className?: string;
  onClick?: () => void;
  showWordmark?: boolean;
  wordmarkClassName?: string;
}

const AppLogo = memo(function AppLogo({
  size = 36,
  className = '',
  onClick,
  showWordmark = false,
  wordmarkClassName = '',
}: AppLogoProps) {
  const containerClassName = useMemo(() => {
    const classes = ['flex items-center'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  return (
    <div className={containerClassName} onClick={onClick}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="HolisticAI.Health logo"
      >
        <defs>
          <linearGradient id="strandL" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#6C5CE7" />
            <stop offset="100%" stopColor="#4CC9F0" />
          </linearGradient>
          <linearGradient id="strandR" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#00F5A0" />
            <stop offset="100%" stopColor="#4CC9F0" />
          </linearGradient>
          <filter id="glow" x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="nodeGlow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="1" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Left strand — sinusoidal path */}
        <path
          d="M16 4 C10 10, 22 14, 16 20 C10 26, 22 30, 16 36 C10 42, 22 46, 16 52"
          stroke="url(#strandL)"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
          filter="url(#glow)"
        />

        {/* Right strand — sinusoidal path (phase-shifted) */}
        <path
          d="M32 4 C38 10, 26 14, 32 20 C38 26, 26 30, 32 36 C38 42, 26 46, 32 52"
          stroke="url(#strandR)"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
          filter="url(#glow)"
        />

        {/* Rungs / base pairs */}
        <line x1="16" y1="10" x2="32" y2="10" stroke="#6C5CE7" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
        <line x1="16" y1="17" x2="32" y2="17" stroke="#4CC9F0" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
        <line x1="16" y1="24" x2="32" y2="24" stroke="#00F5A0" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
        <line x1="16" y1="31" x2="32" y2="31" stroke="#4CC9F0" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
        <line x1="16" y1="38" x2="32" y2="38" stroke="#6C5CE7" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />

        {/* Node dots on left strand */}
        <circle cx="16" cy="10" r="2.5" fill="#6C5CE7" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="1;0.5;1" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="16" cy="17" r="2" fill="#4CC9F0" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="0.6;1;0.6" dur="2.1s" repeatCount="indefinite" />
        </circle>
        <circle cx="16" cy="24" r="2.5" fill="#00F5A0" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="1;0.4;1" dur="1.8s" repeatCount="indefinite" />
        </circle>
        <circle cx="16" cy="31" r="2" fill="#4CC9F0" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="0.5;1;0.5" dur="2.3s" repeatCount="indefinite" />
        </circle>
        <circle cx="16" cy="38" r="2.5" fill="#6C5CE7" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="1;0.6;1" dur="2s" repeatCount="indefinite" />
        </circle>

        {/* Node dots on right strand */}
        <circle cx="32" cy="10" r="2.5" fill="#00F5A0" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="0.5;1;0.5" dur="2.4s" repeatCount="indefinite" />
        </circle>
        <circle cx="32" cy="17" r="2" fill="#4CC9F0" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="1;0.5;1" dur="2.1s" repeatCount="indefinite" />
        </circle>
        <circle cx="32" cy="24" r="2.5" fill="#6C5CE7" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="0.4;1;0.4" dur="1.8s" repeatCount="indefinite" />
        </circle>
        <circle cx="32" cy="31" r="2" fill="#00F5A0" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="1;0.5;1" dur="2.3s" repeatCount="indefinite" />
        </circle>
        <circle cx="32" cy="38" r="2.5" fill="#4CC9F0" filter="url(#nodeGlow)">
          <animate attributeName="opacity" values="0.6;1;0.6" dur="2s" repeatCount="indefinite" />
        </circle>

        {/* Floating animation on whole mark */}
        <animateTransform
          attributeName="transform"
          type="translate"
          values="0,0; 0,-1.5; 0,0"
          dur="3s"
          repeatCount="indefinite"
          additive="sum"
        />
      </svg>

      {showWordmark && (
        <span className={wordmarkClassName || 'font-display font-semibold tracking-tight text-[hsl(210,40%,96%)]'}>
          HolisticAI
          <span className="bg-gradient-to-r from-[#6C5CE7] to-[#00F5A0] bg-clip-text text-transparent">.Health</span>
        </span>
      )}
    </div>
  );
});

export default AppLogo;
